import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { ISlideObservable, Slide, SlideEvent } from './Slide';

const useStyles = makeStyles((theme: Theme) => ({
  bannerContainer: {
    position: 'relative',
    width: 500,
    height: 130,
  },
  smallBannerContainer: {
    position: 'absolute',
    transition: 'left 1000ms ease-out',
    top: 0,
    left: -500,
    width: 200,
    zIndex: 20,
    backgroundColor: '#92D2E8',
    clipPath: 'polygon(0 0, 100% 0%, 89% 100%, 0% 100%)',
  },
  smallBannerVisible: {
    left: 108,
  },
  smallBannerText: {
    color: '#fff',
    fontSize: 16,
    fontStyle: 'italic',
    fontWeight: 'normal',
    margin: 0,
    padding: 0,
    textAlign: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(5),
  },
  largeBannerContainer: {
    position: 'absolute',
    transition: 'left 800ms ease-out',
    top: 16,
    left: -500,
    minWidth: 325,
    zIndex: 10,
    backgroundColor: 'rgb(67,150,200)',
    background:
      'linear-gradient(90deg, rgba(67,150,200,1) 0%, rgba(82,180,215,1) 100%)',
    clipPath: 'polygon(0 0, 100% 0%, 89% 100%, 0% 100%)',
  },
  largeBannerVisible: {
    left: 0,
  },
  largeBannerText: {
    color: '#fff',
    fontSize: 32,
    fontStyle: 'italic',
    fontWeight: 'bold',
    margin: 0,
    padding: theme.spacing(1.5),
    paddingLeft: theme.spacing(15),
    paddingRight: theme.spacing(6),
  },
  logoContainer: {
    position: 'absolute',
    transition: 'left 500ms ease-out',
    top: 8,
    left: -110,
    zIndex: 30,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #bbb',
    borderRadius: '50%',
    width: 80,
    height: 80,
    backgroundColor: '#fff',
    margin: 0,
  },
  logoVisible: {
    left: 20,
  },
}));

export interface IBannerProps {
  isReady: boolean;
  slideObservable: ISlideObservable;
  smallBannerText: string;
  largeBannerText: string;
}

export default function Banner({
  isReady,
  slideObservable,
  smallBannerText,
  largeBannerText,
}: IBannerProps) {
  const classes = useStyles();

  const [isSmallBannerVisible, setIsSmallBannerVisible] = useState(false);
  const [isLargeBannerVisible, setIsLargeBannerVisible] = useState(false);
  const [isLogoVisible, setIsLogoVisible] = useState(false);

  useEffect(() => {
    const observer = (_slide: Slide, event: SlideEvent) => {
      if (event === SlideEvent.animateOutStarted) {
        setIsSmallBannerVisible(false);
        setIsLargeBannerVisible(false);
        setIsLogoVisible(false);
      }
    };

    slideObservable.observe(observer);

    return () => {
      slideObservable.unobserve(observer);
    };
  }, [slideObservable]);

  useEffect(() => {
    if (!isReady) return;

    const smallBannerTimeout = setTimeout(() => {
      setIsSmallBannerVisible(true);
    }, 50);

    const largeBannerTimeout = setTimeout(() => {
      setIsLargeBannerVisible(true);
    }, 500);

    const logoTimeout = setTimeout(() => {
      setIsLogoVisible(true);
    }, 1000);

    return () => {
      clearTimeout(smallBannerTimeout);
      clearTimeout(largeBannerTimeout);
      clearTimeout(logoTimeout);
    };
  }, [isReady]);

  return (
    <div className={classes.bannerContainer}>
      <div
        className={`${classes.smallBannerContainer} ${
          isSmallBannerVisible ? classes.smallBannerVisible : ''
        }`}
      >
        <p className={classes.smallBannerText}>{smallBannerText}</p>
      </div>
      <div
        className={`${classes.largeBannerContainer} ${
          isLargeBannerVisible ? classes.largeBannerVisible : ''
        }`}
      >
        <h1 className={classes.largeBannerText}>{largeBannerText}</h1>
      </div>
      <div
        className={`${classes.logoContainer} ${
          isLogoVisible ? classes.logoVisible : ''
        }`}
      >
        <img
          alt="logo"
          src="/media/leaderboard/squircle.svg"
          width={50}
          height={50}
        />
      </div>
    </div>
  );
}
